// @ts-strict-ignore
import { FilterTags } from "@shared/components/CatalogSearch/FilterTags/FilterTags";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { catalogSearchResultTitle } from "@shared/reactiveVar/useCatalogSearchResultTitle.store";
import type { Taxonomy } from "@shared/types/graphqlGenerated";

import type { Filter } from "../CatalogSearch";

import { IngredientFilterTagsWithExclusions } from "./IngredientFilterTagsWithExclusions/IngredientFilterTagsWithExclusions";

import * as styles from "./SearchFilterTags.styles";

interface Props {
  onClearFilters: () => void;
  maxFilterTagsToShow?: number;
  optimizedTaxonomyTags?: Filter[];
  shouldHideBrands?: boolean;
}

const SearchFilterTags = ({
  onClearFilters,
  maxFilterTagsToShow,
  optimizedTaxonomyTags = [],
  shouldHideBrands = false,
}: Props) => {
  const {
    activeFilters,
    isOnlyShowInStock,
    setIsOnlyShowInStock,
    isOnlyShowFavorites,
    setIsOnlyShowFavorites,
    removeActiveFilter,
    activeBrands,
    activeTaxonomies,
    setActiveTaxonomies,
    activeOptimizedTaxonomies,
    setActiveOptimizedTaxonomies,
    setActiveBrands,
    activeSupplementTypes,
    setActiveSupplementTypes,
    activeConditions,
    setActiveConditions,
    activeCategories,
    setActiveCategories,
    isFsTested,
    setIsFsTested,
  } = useCatalogSearch();

  const brandTags: Filter[] = shouldHideBrands
    ? []
    : activeBrands.map(brand => ({
        name: brand.name,
        category: "Brand",
        id: brand.id,
      }));

  const handleOnRemoveBrands = (filter: Filter) =>
    setActiveBrands(activeBrands.filter(activeBrand => activeBrand.id !== filter.id));

  const taxonomyTags: Filter[] =
    activeTaxonomies?.map(taxonomy => ({
      name: taxonomy.name,
      category: "Taxonomy",
      id: taxonomy.id,
    })) ?? [];

  const handleOnRemoveTaxonomy = (filter: Filter) =>
    setActiveTaxonomies(activeTaxonomies.filter(activeTaxonomy => activeTaxonomy.id !== filter.id));

  const handleOnRemoveOptimizedTaxonomy = (filter: Filter) => {
    const searchResultTitle = catalogSearchResultTitle();
    const filterOptimizedTaxonomies = activeOptimizedTaxonomies.filter(
      tax => tax.type === "filter"
    );
    if (filterOptimizedTaxonomies.length === 1 && searchResultTitle.type === "taxonomy") {
      const searchOptimizedTaxonomies = activeOptimizedTaxonomies.filter(
        tax => tax.type === "search"
      );
      setActiveOptimizedTaxonomies([
        {
          name: (searchResultTitle.title as Taxonomy).name,
          id: (searchResultTitle.title as Taxonomy).id,
          hideFromDisplay: true,
          type: "lock",
        },
        ...searchOptimizedTaxonomies,
      ]);

      return;
    }

    const updatedFilterTaxonomies = activeOptimizedTaxonomies.filter(
      tax => tax.type === "filter" && tax.id !== filter.id
    );

    const searchTaxonomies = activeOptimizedTaxonomies.filter(tax => tax.type === "search");

    setActiveOptimizedTaxonomies([...updatedFilterTaxonomies, ...searchTaxonomies]);
  };

  const supplementTypesTags: Filter[] = activeSupplementTypes.map(supplementType => ({
    name: supplementType.name,
    category: "SupplementType",
    id: supplementType.id,
  }));

  const handleOnRemoveSupplementTypes = (filter: Filter) =>
    setActiveSupplementTypes(
      activeSupplementTypes.filter(activeSupplementType => activeSupplementType.id !== filter.id)
    );

  const handleOnRemoveConditions = (filter: Filter) =>
    setActiveConditions(
      activeConditions.filter(activeCondition => activeCondition.id !== filter.id)
    );

  const inStockTag: Filter[] = isOnlyShowInStock
    ? [
        {
          name: "In Stock",
          category: "InStock",
          id: "InStockId",
        },
      ]
    : [];

  const handleOnRemoveInStockTag = () => setIsOnlyShowInStock(false);

  const favoriteTag: Filter[] = isOnlyShowFavorites
    ? [
        {
          name: "All Favorites",
          category: "Favorite",
          id: "FavoriteId",
        },
      ]
    : [];

  const handleOnRemoveFavoriteTag = () => setIsOnlyShowFavorites(false);

  const fullscriptTestedTag: Filter[] = isFsTested
    ? [
        {
          name: "Fullscript Tested",
          category: "FullscriptTested",
          id: "FullscriptTestedId",
        },
      ]
    : [];

  const handleOnRemoveFullscriptTestedTag = () => setIsFsTested(false);

  const categoryTags: Filter[] =
    activeCategories?.map(category => ({
      name: category.name,
      category: "Category",
      id: category.id,
    })) ?? [];

  const handleOnRemoveCategory = (filter: Filter) =>
    setActiveCategories(
      activeCategories?.filter(activeCategory => activeCategory.id !== filter.id)
    );

  const conditionsTags: Filter[] =
    activeConditions?.map(condition => ({
      name: condition.name,
      category: "Condition",
      id: condition.id,
    })) ?? [];

  const filterTags = [
    ...inStockTag,
    ...favoriteTag,
    ...activeFilters,
    ...brandTags,
    ...supplementTypesTags,
    ...conditionsTags,
    ...categoryTags,
    ...taxonomyTags,
    ...optimizedTaxonomyTags,
    ...fullscriptTestedTag,
  ];

  const onRemoveActiveFilter = (filter: Filter) => {
    switch (filter.category) {
      case "Brand":
        handleOnRemoveBrands(filter);
        break;
      case "SupplementType":
        handleOnRemoveSupplementTypes(filter);
        break;
      case "Condition":
        handleOnRemoveConditions(filter);
        break;
      case "Category":
        handleOnRemoveCategory(filter);
        break;
      case "Taxonomy":
        handleOnRemoveTaxonomy(filter);
        break;
      case "OptimizedTaxonomy":
        handleOnRemoveOptimizedTaxonomy(filter);
        break;
      case "InStock":
        handleOnRemoveInStockTag();
        break;
      case "Favorite":
        handleOnRemoveFavoriteTag();
        break;
      case "FullscriptTested":
        handleOnRemoveFullscriptTestedTag();
        break;
      default:
        removeActiveFilter(filter);
        break;
    }
  };

  return (
    <div css={styles.searchesWrapper}>
      <IngredientFilterTagsWithExclusions />
      <FilterTags
        filterTags={filterTags}
        onClearAllFilterTags={onClearFilters}
        onRemoveFilterTag={onRemoveActiveFilter}
        maxFilterTagsToShow={maxFilterTagsToShow}
      />
    </div>
  );
};

export { SearchFilterTags };
